import NotificationSkeleton from '@/components/skeletons/NotificationSkeleton'
import { getAssetDashboard } from '@/modules/Asset/services'
import { AssetDetail } from '@/modules/Asset/types'
import { DocumentGroup } from '@/modules/Asset/constants'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'
import { Button } from '@/components/ui/button'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Eye, Inbox } from 'lucide-react'
import AssetCard from './AssetCard'
interface InternalInformationContentProps {
    group: DocumentGroup
}

const buildQueryParams = (group: DocumentGroup) => {
    return btoa(
        JSON.stringify({
            document_group: [group],
        })
    )
}

const InternalInformationContent: React.FC<InternalInformationContentProps> = ({
    group,
}) => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()

    const { data: documents, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.ASSETS_DASHBOARD, group],
            queryFn: () => getAssetDashboard([group]),
            enabled: !!group,
        },
        {
            loadingComponent: <NotificationSkeleton />,
            errorDisplayMode: 'page',
        }
    )

    const handleViewMore = () => {
        navigate(paths.documentList + '?filters=' + buildQueryParams(group))
    }

    const handleAssetClick = (slug: string) => {
        navigate(generatePathDatacentral(paths.assetDetail, { slug: slug }))
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const displayedDocuments = documents?.results?.slice(0, 5) || []
    const hasMoreItems = (documents?.results?.length || 0) > 5

    return (
        <div className="space-y-3">
            <div className="xs:max-h-[300px] md:max-h-[520px] xs:min-h-[300px] md:min-h-[520px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 px-1">
                <div className="space-y-3 py-1">
                    {displayedDocuments?.map(
                        (asset: AssetDetail, index: number) => (
                            <AssetCard
                                key={index}
                                asset={asset}
                                onClick={() =>
                                    handleAssetClick(asset.asset_slug)
                                }
                            />
                        )
                    )}

                    {(!documents?.results ||
                        documents?.results?.length === 0) && (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <Inbox className="w-10 h-10 mb-2" />
                            <Typogaphy className="text-sm">
                                {t('notifications.no_data')}
                            </Typogaphy>
                        </div>
                    )}
                </div>
            </div>

            {/* View More Button */}
            {hasMoreItems && (
                <Button
                    onClick={handleViewMore}
                    variant="outline"
                    size="sm"
                    className="w-full gap-2"
                >
                    <Eye className="w-4 h-4" />
                    {t('view_all')}
                </Button>
            )}
        </div>
    )
}

export default InternalInformationContent
